import { Loader2 } from "lucide-react";
import type React from "react";
import { useRef } from "react";
import { useArticleStore } from "../store/useArticleStore";
import { isAbortError } from "../utils/errors";
import { updateArticle } from "../utils/updateArticle";

export const GenerateButton: React.FC = () => {
  const { article, setArticle } = useArticleStore();
  const requestController = useRef<AbortController | null>(null);

  const handleGenerateArticle = async () => {
    if (article.isLoading) {
      requestController.current?.abort();
      return;
    }

    const controller = new AbortController();
    requestController.current = controller;
    try {
      await updateArticle(article, setArticle, { signal: controller.signal });
    } catch (error) {
      if (!isAbortError(error)) console.error("Failed to generate article:", error);
    } finally {
      if (requestController.current === controller) requestController.current = null;
    }
  };

  return (
    <button
      type="button"
      onClick={handleGenerateArticle}
      disabled={article.sections.length === 0}
      aria-live="polite"
      className={`py-3 px-4 rounded-lg font-medium text-white
        ${
          article.sections.length === 0
            ? "bg-gray-400 cursor-not-allowed"
            : article.isLoading
              ? "bg-red-500 hover:bg-red-600"
              : "bg-blue-500 hover:bg-blue-600"
        }`}
    >
      {article.isLoading ? (
        <span className="flex items-center justify-center">
          <Loader2 aria-hidden="true" className="animate-spin mr-2 h-5 w-5" />
          {article.progress
            ? `Cancel (${article.progress.current}/${article.progress.total})`
            : "Cancel"}
        </span>
      ) : article.sections.some((section) => section.description) ? (
        "Update"
      ) : (
        "Generate"
      )}
    </button>
  );
};
